/**
 * 오프라인 사진 큐 → 자동 업로드 동기화 (P2-8).
 *
 * 트리거:
 *  1) navigator.onLine === false 상태에서 업로드 호출 → 큐에 저장 + 즉시 종료
 *  2) window 'online' 이벤트 발생 시 → flushQueue() 자동 실행
 *  3) 페이지 진입 시 → 미처리 잔여 큐를 flushQueue()
 *  4) 30초 주기 폴링 (페이지 활성 시) — 'online' 이벤트가 누락된 케이스 보호
 *
 * 동시 flush 방지: _flushing 플래그.
 * 이미 업로드된 사진은 큐에서 제거. 실패는 markRetry 로 retry_count 증가.
 *
 * 의도적으로 retry_count 상한이나 backoff 는 안 둠 (현장 작업자가 직접 큐를 보고
 * 판단 — 자동 폐기는 데이터 손실 위험이 더 큼).
 */

import { uploadSitePhoto } from '@/lib/api/sitePhotos'

import {
  enqueuePhoto,
  getQueuedPhotos,
  markRetry,
  QueuedPhoto,
  removeFromQueue,
} from './photoQueue'

type Listener = () => void
const _listeners = new Set<Listener>()
let _flushing = false
let _pollTimer: number | null = null
// 토큰 만료(401) 감지 시 true — 자동 재시도 중단, UI 가 "재로그인 필요" 표시
let _needsReauth = false

/** 큐 상태 변경 알림 — React hook 이 구독해서 re-render. */
export function subscribe(listener: Listener): () => void {
  _listeners.add(listener)
  return () => _listeners.delete(listener)
}

/** 토큰 만료로 자동 업로드가 멈췄는지. */
export function getNeedsReauth(): boolean {
  return _needsReauth
}

/** 재로그인 후 호출 — 재인증 플래그 해제. */
export function clearReauthFlag(): void {
  if (_needsReauth) {
    _needsReauth = false
    _notify()
  }
}

function _isAuthError(err: unknown): boolean {
  const e = err as { response?: { status?: number } }
  return e?.response?.status === 401
}

function _notify() {
  _listeners.forEach((l) => {
    try {
      l()
    } catch (err) {
      console.warn('[sync] listener error', err)
    }
  })
}

/**
 * 업로드 시도. 오프라인 또는 실패 시 IndexedDB 큐에 저장.
 * 반환: { uploaded: true } 또는 { queued: true, id: number }
 */
export async function uploadOrQueue(args: {
  projectId: string
  file: File
}): Promise<{ uploaded: true } | { queued: true; id: number }> {
  const { projectId, file } = args
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const id = await enqueuePhoto({
      projectId,
      filename: file.name,
      blob: file,
    })
    _notify()
    return { queued: true, id }
  }
  try {
    await uploadSitePhoto({ projectId, file })
    return { uploaded: true }
  } catch (err) {
    // 네트워크 오류 또는 토큰 만료(401) 면 큐로 우회 — 사진 유실 방지.
    // 401 은 재인증 플래그도 세움. 422/403 등은 그대로 throw.
    if (_isNetworkError(err) || _isAuthError(err)) {
      if (_isAuthError(err)) {
        _needsReauth = true
      }
      const id = await enqueuePhoto({
        projectId,
        filename: file.name,
        blob: file,
      })
      _notify()
      return { queued: true, id }
    }
    throw err
  }
}

function _isNetworkError(err: unknown): boolean {
  if (!err) return false
  const e = err as { message?: string; code?: string; response?: unknown }
  // axios: response 없으면 네트워크. fetch: TypeError 'Failed to fetch'
  if (e.response === undefined && e.message) return true
  if (e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED') return true
  return false
}

/** 큐에 쌓인 사진 1건씩 순차 업로드. 동시 호출은 무시. */
export async function flushQueue(): Promise<{
  uploaded: number
  failed: number
  remaining: number
}> {
  if (_flushing) return { uploaded: 0, failed: 0, remaining: -1 }
  // 토큰 만료 상태면 자동 재시도 안 함 (30초마다 무한 401 방지).
  // 사용자가 재로그인 후 수동 flush 하면 clearReauthFlag 로 풀림.
  if (_needsReauth) {
    return { uploaded: 0, failed: 0, remaining: (await getQueuedPhotos()).length }
  }
  _flushing = true
  let uploaded = 0
  let failed = 0
  try {
    const items = await getQueuedPhotos()
    for (const item of items) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) break
      try {
        const file = _blobToFile(item)
        await uploadSitePhoto({ projectId: item.project_id, file })
        await removeFromQueue(item.id)
        uploaded += 1
        _notify()
      } catch (err) {
        // 토큰 만료 — 재시도해도 계속 실패. 플래그 세우고 즉시 중단.
        if (_isAuthError(err)) {
          _needsReauth = true
          _notify()
          break
        }
        const msg = (err as { message?: string })?.message ?? String(err)
        await markRetry(item.id, msg)
        failed += 1
        _notify()
        // 네트워크 끊김이면 즉시 중단 (다음 'online' 이벤트에서 재시도)
        if (_isNetworkError(err)) break
      }
    }
  } finally {
    _flushing = false
  }
  const remaining = (await getQueuedPhotos()).length
  return { uploaded, failed, remaining }
}

function _blobToFile(item: QueuedPhoto): File {
  return new File([item.blob], item.filename, {
    type: item.blob.type || 'image/jpeg',
    lastModified: item.added_at,
  })
}

/**
 * 글로벌 초기화 — 앱 부팅 시 1회.
 *  - 'online' 이벤트 → flushQueue
 *  - 30초 주기 폴링 (백그라운드 탭 무시)
 *  - 페이지 visible 전환 시 1회 flush
 */
export function initOfflineSync() {
  if (typeof window === 'undefined') return
  // 중복 초기화 방지
  if ((window as unknown as { __seolhwaSyncReady?: boolean }).__seolhwaSyncReady) return
  ;(window as unknown as { __seolhwaSyncReady?: boolean }).__seolhwaSyncReady = true

  window.addEventListener('online', () => {
    flushQueue().catch((e) => console.warn('[sync] online flush failed', e))
  })

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      flushQueue().catch(() => {})
    }
  })

  // 초기 1회 — 페이지 로드 시 잔여 큐 처리
  if (navigator.onLine) {
    flushQueue().catch(() => {})
  }

  // 30초 폴링 (브라우저 'online' 이벤트가 누락되는 환경 보호)
  _pollTimer = window.setInterval(() => {
    if (document.visibilityState !== 'visible') return
    if (!navigator.onLine) return
    flushQueue().catch(() => {})
  }, 30_000)
}

/** 테스트/teardown 용. */
export function stopOfflineSync() {
  if (_pollTimer != null) {
    clearInterval(_pollTimer)
    _pollTimer = null
  }
}
